import { format } from 'date-fns/format'
import {
  type FieldInputProps,
  type FieldProps,
  type FormikErrors,
  type FormikTouched,
  Field,
  getIn,
} from 'formik'
import { useTranslation } from 'next-i18next'
import { type MouseEvent, useCallback } from 'react'
import { useTheme } from 'styled-components'

import { TRANSLATION_KEYS } from '@/constants/localization'
import {
  type AvailableDate,
  type FormValues,
} from '@/features/listing/components/request-viewing/RequestViewForm/RequestViewingForm'
import { AvailableDatesContainer } from '@/features/listing/components/request-viewing/RequestViewForm/styled'
import { MAX_AVAILABLE_DATES } from '@/features/listing/components/request-viewing/RequestViewing'
import { Flex } from '@/ui/atoms/flex'
import { HeadlineText } from '@/ui/atoms/headline-text'
import { Spacing } from '@/ui/atoms/spacing'
import { TextLink } from '@/ui/atoms/text-link'
import { DateField } from '@/ui/molecules/date-field'
import { validationFnPerField } from '@/utils/validation'

type AvailableDatesFieldProps = {
  index: number
  remove: (index: number) => void
  errors: FormikErrors<FormValues>
  values: FormValues
  touched: FormikTouched<FormValues>
}

export const AvailableDatesField = ({
  index,
  remove,
  values,
  errors,
  touched,
}: AvailableDatesFieldProps) => {
  const { colors, spacing } = useTheme()
  const { t } = useTranslation([TRANSLATION_KEYS.COMMON, TRANSLATION_KEYS.REQUEST_VIEWING])

  const getError = useCallback(
    (field: FieldInputProps<FormValues>) => {
      return getIn(errors, field.name) && getIn(touched, field.name)
        ? t(String(getIn(errors, field.name)))
        : undefined
    },
    [errors, t, touched]
  )

  const removeAvailableDateField = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    remove(index)
  }

  const handleTimeFromValidate = (value: AvailableDate['timeFrom']) => {
    return validationFnPerField.timeFrom(value, values.availableDates[index].timeTo)
  }
  const handleTimeToValidate = (value: AvailableDate['timeTo']) => {
    return validationFnPerField.timeTo(value, values.availableDates[index].timeFrom)
  }

  return (
    <AvailableDatesContainer pTop={spacing.defaultSmall} hasDivider={index > 0}>
      <Flex justifyContent="space-between" alignItems="center">
        <HeadlineText size="small" color={colors.grey.primary}>
          {t('request_viewing:form.availableDateField.title', {
            currentOption: index + 1,
            maxOptions: MAX_AVAILABLE_DATES,
          })}
        </HeadlineText>

        {index !== 0 ? (
          <TextLink hasUnderline variant="secondary" as="button" onClick={removeAvailableDateField}>
            {t('request_viewing:form.availableDateField.remove')}
          </TextLink>
        ) : null}
      </Flex>
      <Spacing mTop={spacing.default}>
        <Field name={`availableDates.${index}.date`} validate={validationFnPerField.futureDate}>
          {({ field }: FieldProps) => (
            <DateField
              label={t('request_viewing:form.availableDateField.date')}
              id={`availableDates.${index}.date`}
              error={getError(field)}
              min={format(new Date(), 'yyyy-MM-dd')}
              {...field}
            />
          )}
        </Field>

        <Field name={`availableDates.${index}.timeFrom`} validate={handleTimeFromValidate}>
          {({ field }: FieldProps) => (
            <DateField
              type="time"
              label={t('request_viewing:form.availableDateField.timeFrom')}
              id={`availableDates.${index}.timeFrom`}
              error={getError(field)}
              {...field}
            />
          )}
        </Field>

        <Field name={`availableDates.${index}.timeTo`} validate={handleTimeToValidate}>
          {({ field }: FieldProps) => (
            <DateField
              type="time"
              label={t('request_viewing:form.availableDateField.timeTo')}
              id={`availableDates.${index}.timeTo`}
              error={getError(field)}
              {...field}
            />
          )}
        </Field>
      </Spacing>
    </AvailableDatesContainer>
  )
}
